import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, UserCheck, Users as UsersIcon, Briefcase } from "@phosphor-icons/react";

const CHANNEL_OPTIONS = [
  { value: "meta", label: "Meta" },
  { value: "walk_in", label: "Walk In" },
  { value: "telephonic", label: "Telephonic" },
  { value: "indiamart", label: "IndiaMart" },
  { value: "csv_upload", label: "CSV Upload" },
  { value: "other", label: "Other" },
];

const DEFAULT_SERVICE_TYPES = [
  { value: "warranty", label: "Warranty" },
  { value: "out_of_warranty", label: "Out of Warranty" },
  { value: "amc", label: "AMC" },
  { value: "out_of_amc", label: "Out of AMC" },
  { value: "on_call", label: "On Call" },
  { value: "register", label: "Register" },
  { value: "other", label: "Other" },
];

const DEPARTMENT_OPTIONS = [
  { value: "all", label: "Both (Lead & Service)" },
  { value: "lead", label: "Lead" },
  { value: "service", label: "Service" },
];

const DEPARTMENT_BADGES = {
  all: { label: "Lead & Service", color: "bg-blue-50 text-[#002FA7] border-blue-200" },
  both: { label: "Lead & Service", color: "bg-blue-50 text-[#002FA7] border-blue-200" },
  lead: { label: "Lead", color: "bg-purple-50 text-purple-700 border-purple-200" },
  service: { label: "Service", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState(DEFAULT_SERVICE_TYPES);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deptFilter, setDeptFilter] = useState("all");

  const load = useCallback(() => api.get("/users").then(r => setUsers(r.data)), []);

  useEffect(() => {
    load();
    api.get("/service-types")
      .then(r => {
        if (r.data && r.data.length > 0) {
          const list = r.data.filter(t => t.is_active !== false).map(t => ({
            value: t.key,
            label: t.name,
            code: t.code
          }));
          if (list.length > 0) setServiceTypes(list);
        }
      })
      .catch(() => { });
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setChannel = async (id, value) => {
    setSavingId(id);
    try {
      await api.patch(`/users/${id}`, { lead_assign_for: value });
      toast.success("Lead Channel updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingId(null); }
  };

  const setServiceAssign = async (id, value) => {
    setSavingId(id);
    try {
      await api.patch(`/users/${id}`, { service_assign_for: value });
      toast.success("Service Type updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingId(null); }
  };

  const setDepartment = async (id, value) => {
    setSavingId(id);
    try {
      await api.patch(`/users/${id}`, { department: value });
      toast.success("Department updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingId(null); }
  };

  const filteredUsers = users.filter(u => {
    if (deptFilter === "all") return true;
    const dept = u.department || "all";
    if (deptFilter === "lead") return dept === "lead" || dept === "all" || dept === "both";
    if (deptFilter === "service") return dept === "service" || dept === "all" || dept === "both";
    return true;
  });

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">Team</h1>
          <p className="text-sm text-[#6B7280] mt-1">Manage team members, roles, and assignment filters for Lead & Service CRM.</p>
        </div>
        {user.role === "admin" && (
          <button onClick={() => setShowAdd(true)} data-testid="add-user-btn"
            className="bg-[#002FA7] text-white font-bold px-5 py-2.5 hover:bg-[#002280] flex items-center gap-2 shadow-xs transition-colors self-start sm:self-auto">
            <Plus size={16} weight="bold" /> Add Team Member
          </button>
        )}
      </div>

      {/* Scope / Department Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
        <span className="text-[#6B7280] uppercase tracking-wider text-[11px] mr-1">Filter by Scope:</span>
        {[
          { key: "all", label: "All Members", count: users.length },
          { key: "lead", label: "Lead CRM Eligible", count: users.filter(u => !u.department || u.department === "lead" || u.department === "all" || u.department === "both").length },
          { key: "service", label: "Service CRM Eligible", count: users.filter(u => !u.department || u.department === "service" || u.department === "all" || u.department === "both").length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setDeptFilter(tab.key)}
            className={`px-3 py-1.5 border transition-all ${deptFilter === tab.key ? "bg-[#002FA7] text-white border-[#002FA7] shadow-xs" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <div className="border border-[#E5E7EB] bg-white overflow-x-auto shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b-2 border-[#111827] bg-[#F9FAFB]">
              {["Name", "Email", "Phone", "Role", "Assign Scope (Lead / Service)", "Assigned Types (Lead / Service)", ""].map(h => (
                <th key={h} className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-sm text-[#6B7280]">
                  No team members match the selected filter.
                </td>
              </tr>
            ) : (
              filteredUsers.map(u => {
                const deptKey = u.department || "all";
                const badge = DEPARTMENT_BADGES[deptKey] || DEPARTMENT_BADGES.all;
                const handlesLead = u.role === "salesperson" && (deptKey === "lead" || deptKey === "all" || deptKey === "both");
                const handlesService = u.role === "salesperson" && (deptKey === "service" || deptKey === "all" || deptKey === "both");

                return (
                  <tr key={u.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 font-bold text-sm text-[#111827]">{u.name}</td>
                    <td className="py-3 px-4 text-sm font-mono text-gray-700">{u.email}</td>
                    <td className="py-3 px-4 text-sm font-mono text-gray-700">{u.phone || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[#111827] bg-white">
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs font-bold whitespace-nowrap">
                      {u.role === "salesperson" && user.role === "admin" ? (
                        <select
                          value={deptKey}
                          onChange={(e) => setDepartment(u.id, e.target.value)}
                          disabled={savingId === u.id}
                          className="border border-[#D1D5DB] px-2.5 py-1 text-xs font-bold bg-white text-slate-800 outline-none focus:border-[#002FA7]"
                        >
                          <option value="all">Both (Lead & Service)</option>
                          <option value="lead">Lead</option>
                          <option value="service">Service</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${badge.color}`}>
                          {badge.label}
                        </span>
                      )}
                    </td>

                    {/* Assigned Channels & Service Types */}
                    <td className="py-3 px-4 text-xs font-bold whitespace-nowrap">
                      {u.role === "salesperson" ? (
                        <div className="flex flex-col gap-1.5">
                          {handlesLead && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] uppercase text-purple-700 font-bold w-12 shrink-0">Lead:</span>
                              {user.role === "admin" ? (
                                <select
                                  value={u.lead_assign_for || ""}
                                  onChange={(e) => setChannel(u.id, e.target.value)}
                                  disabled={savingId === u.id}
                                  data-testid={`channel-select-${u.id}`}
                                  className={`border px-2 py-1 text-xs font-bold bg-white ${u.lead_assign_for ? "border-[#D1D5DB]" : "border-[#EF4444] text-[#EF4444]"} disabled:opacity-60`}
                                >
                                  <option value="" disabled>— pick lead channel —</option>
                                  {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              ) : (
                                <span className="text-xs text-gray-700 font-mono">
                                  {CHANNEL_OPTIONS.find(o => o.value === u.lead_assign_for)?.label || u.lead_assign_for || "—"}
                                </span>
                              )}
                            </div>
                          )}

                          {handlesService && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] uppercase text-emerald-800 font-bold w-12 shrink-0">Service:</span>
                              {user.role === "admin" ? (
                                <select
                                  value={u.service_assign_for || "warranty"}
                                  onChange={(e) => setServiceAssign(u.id, e.target.value)}
                                  disabled={savingId === u.id}
                                  data-testid={`service-type-select-${u.id}`}
                                  className="border border-[#D1D5DB] px-2 py-1 text-xs font-bold bg-white disabled:opacity-60"
                                >
                                  <option value="all">All Service Types</option>
                                  {serviceTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              ) : (
                                <span className="text-xs text-gray-700 font-mono">
                                  {u.service_assign_for === "all" ? "All Service Types" : serviceTypes.find(o => o.value === u.service_assign_for)?.label || u.service_assign_for || "All"}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#6B7280]">n/a</span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {user.role === "admin" && (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setEditUser(u)} data-testid={`edit-user-${u.id}`} className="text-[#002FA7] hover:bg-blue-50 p-2 border border-transparent hover:border-blue-200 transition-colors" title="Edit User">
                            <PencilSimple size={15} weight="bold" />
                          </button>
                          {u.id !== user.id && (
                            <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-[#EF4444] hover:bg-red-50 p-2 border border-transparent hover:border-red-200 transition-colors" title="Delete User">
                              <Trash size={15} weight="bold" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showAdd && <AddUserModal serviceTypes={serviceTypes} onClose={() => setShowAdd(false)} onCreated={load} />}
      {editUser && <EditUserModal serviceTypes={serviceTypes} userToEdit={editUser} onClose={() => setEditUser(null)} onUpdated={load} />}
    </div>
  );
}

function AddUserModal({ serviceTypes, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    role: "salesperson",
    department: "lead",
    password: "",
    lead_assign_for: "walk_in",
    service_assign_for: "warranty"
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const showLeadType = f.role === "salesperson" && (f.department === "lead" || f.department === "all" || f.department === "both");
  const showServiceType = f.role === "salesperson" && (f.department === "service" || f.department === "all" || f.department === "both");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f };
      if (payload.role !== "salesperson") {
        delete payload.lead_assign_for;
        delete payload.service_assign_for;
      } else {
        if (!showLeadType) delete payload.lead_assign_for;
        if (!showServiceType) delete payload.service_assign_for;
      }
      await api.post("/users", payload);
      toast.success("Team member created successfully"); onCreated(); onClose();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-[#111827] w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mb-4 text-[#111827]">Add Team Member</h3>
        <form onSubmit={submit} className="space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input type="text" value={f.name} onChange={(e) => set("name", e.target.value)} required placeholder="e.g. Rahul Sharma"
                data-testid="new-user-name"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required placeholder="user@example.com"
                data-testid="new-user-email"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Phone / WhatsApp</label>
              <input type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit phone"
                data-testid="new-user-phone"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required placeholder="••••••••"
                data-testid="new-user-password"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Role</label>
              <select value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="new-user-role"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium">
                <option value="salesperson">Salesperson</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Assign For (Scope) <span className="text-red-500">*</span>
              </label>
              <select
                value={f.department || "all"}
                onChange={(e) => set("department", e.target.value)}
                data-testid="new-user-department"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium text-[#002FA7]"
              >
                {DEPARTMENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Conditional Dropdown: Lead Channel */}
          {showLeadType && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Primary Lead Channel <span className="text-purple-700 font-bold">(Lead CRM)</span>
              </label>
              <select
                value={f.lead_assign_for || "walk_in"}
                onChange={(e) => set("lead_assign_for", e.target.value)}
                data-testid="new-user-lead-assign-for"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium"
              >
                {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-[#6B7280] mt-1">This salesperson will primarily receive auto-assigned leads from this channel.</p>
            </div>
          )}

          {/* Conditional Dropdown: Service Type (from Service Master) */}
          {showServiceType && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Primary Service Type <span className="text-emerald-800 font-bold">(Service Master)</span>
              </label>
              <select
                value={f.service_assign_for || "warranty"}
                onChange={(e) => set("service_assign_for", e.target.value)}
                data-testid="new-user-service-assign-for"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium"
              >
                <option value="all">All Service Types</option>
                {serviceTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-[#6B7280] mt-1">Service contracts matching this type will be prioritized for this team member.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" onClick={onClose} className="border border-[#D1D5DB] px-5 py-2 text-sm font-bold hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="new-user-submit"
              className="bg-[#002FA7] text-white font-bold px-5 py-2 hover:bg-[#002280] disabled:opacity-60 transition-colors">
              {saving ? "Saving..." : "Create Member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ serviceTypes, userToEdit, onClose, onUpdated }) {
  const [f, setF] = useState({
    name: userToEdit.name,
    email: userToEdit.email,
    phone: userToEdit.phone || "",
    role: userToEdit.role,
    department: userToEdit.department || "all",
    password: "",
    lead_assign_for: userToEdit.lead_assign_for || "walk_in",
    service_assign_for: userToEdit.service_assign_for || "warranty"
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const showLeadType = f.role === "salesperson" && (f.department === "lead" || f.department === "all" || f.department === "both");
  const showServiceType = f.role === "salesperson" && (f.department === "service" || f.department === "all" || f.department === "both");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f };
      if (payload.role !== "salesperson") {
        delete payload.lead_assign_for;
        delete payload.service_assign_for;
      } else {
        if (!showLeadType) delete payload.lead_assign_for;
        if (!showServiceType) delete payload.service_assign_for;
      }
      if (!payload.password) delete payload.password;
      await api.patch(`/users/${userToEdit.id}`, payload);
      toast.success("User updated successfully"); onUpdated(); onClose();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update user"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-[#111827] w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mb-4 text-[#111827]">Edit Team Member</h3>
        <form onSubmit={submit} className="space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Full Name</label>
              <input type="text" value={f.name} onChange={(e) => set("name", e.target.value)} required
                data-testid="edit-user-name"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Email</label>
              <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required
                data-testid="edit-user-email"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Phone</label>
              <input type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit phone"
                data-testid="edit-user-phone"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">New Password (optional)</label>
              <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="Leave blank to keep"
                data-testid="edit-user-password"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Role</label>
              <select value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="edit-user-role"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium">
                <option value="salesperson">Salesperson</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">Assign For (Scope)</label>
              <select
                value={f.department || "all"}
                onChange={(e) => set("department", e.target.value)}
                data-testid="edit-user-department"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium text-[#002FA7]"
              >
                {DEPARTMENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Conditional Dropdown: Lead Channel */}
          {showLeadType && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Primary Lead Channel <span className="text-purple-700 font-bold">(Lead CRM)</span>
              </label>
              <select
                value={f.lead_assign_for || "walk_in"}
                onChange={(e) => set("lead_assign_for", e.target.value)}
                data-testid="edit-user-lead-assign-for"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium"
              >
                {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {/* Conditional Dropdown: Service Type (from Service Master) */}
          {showServiceType && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Primary Service Type <span className="text-emerald-800 font-bold">(Service Master)</span>
              </label>
              <select
                value={f.service_assign_for || "warranty"}
                onChange={(e) => set("service_assign_for", e.target.value)}
                data-testid="edit-user-service-assign-for"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium"
              >
                <option value="all">All Service Types</option>
                {serviceTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" onClick={onClose} className="border border-[#D1D5DB] px-5 py-2 text-sm font-bold hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="edit-user-submit"
              className="bg-[#002FA7] text-white font-bold px-5 py-2 hover:bg-[#002280] disabled:opacity-60 transition-colors">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
